import type { ComplexSpec, EntitySpec, ModelSpec, OwnedSpec, PropertySpec, RelationshipSpec } from '@modelviewer/core';
import { ArrowLeft, ArrowRight, Braces, Crosshair, KeyRound, Link2, Package, Table2 } from 'lucide-react';
import { CascadeIcon } from '../diagram/DeletePanel';
import { locate, typeLabel } from '../diagram/graph';
import { cn } from '../lib/cn';
import { openSourceFile, tabKind, useActiveTab } from '../state/dock';
import { refKey, revealEntity, select, useWorkspace, type LoadedModel, type ModelRef } from '../state/workspace';
import { MentionedIn, NoteInspector } from '../notes/NoteInspector';
import { SketchInspector, SketchSummary } from '../sketch/SketchInspector';
import { ChangePanel } from '../timeline/ChangePanel';
import { EntityHistory } from '../timeline/EntityHistory';
import { useTimeline } from '../timeline/timeline';
import { CodeInspector } from '../code/CodeInspector';
import { Field, Mono, Section, SourceLink } from './InspectorParts';

type Container = EntitySpec | OwnedSpec | ComplexSpec;

/** Resolves a diagram row id (`Url`, `Address.City`, `CreditLimit.Amount`) inside an entity. */
function resolveRow(entity: EntitySpec, rowId: string): { container: Container; property: PropertySpec; name: string; path: string[] } | null {
  const parts = rowId.split('.');
  const name = parts.pop()!;
  let container: Container = entity;
  for (const part of parts) {
    const next: Container | undefined = ('owned' in container ? container.owned?.[part] : undefined) ?? container.complex?.[part];
    if (!next) return null;
    container = next;
  }
  const property = container.properties[name];
  return property ? { container, property, name, path: parts } : null;
}

function rootOf(name: string, model: ModelSpec): string {
  return locate(model, name)?.node ?? name;
}

function deleteLabel(r: RelationshipSpec): string {
  const map: Record<string, string> = {
    cascade: 'Cascade (dependents are deleted)',
    clientCascade: 'Cascade, in EF only',
    restrict: 'Restrict (delete is blocked)',
    setNull: 'Set null',
    clientSetNull: 'Set null, in EF only',
    noAction: 'No action',
    clientNoAction: 'No action, in EF only',
  };
  return map[r.onDelete] ?? r.onDelete;
}

function RelationshipRow({ r, index, focus, modelRef }: { r: RelationshipSpec; index: number; focus: string; modelRef: ModelRef }) {
  const outgoing = r.dependent === focus || r.dependent.startsWith(`${focus}.`);
  const other = outgoing ? r.principal : r.dependent;
  const many = r.cardinality === 'one-to-many';
  const text = outgoing ? `many ${r.dependent} → one ${r.principal}` : `one ${r.principal} → ${many ? 'many' : 'one'} ${r.dependent}`;
  return (
    <button
      onClick={() => select(modelRef, { kind: 'relationship', index })}
      className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover"
      title={text}
    >
      <Link2 size={13} className="flex-none text-faint" />
      <span className="min-w-0 flex-1 truncate">
        {outgoing ? (r.dependentNavigation ?? r.foreignKey.join(', ')) : (r.principalNavigation ?? `(${r.dependent})`)}
      </span>
      <span className="flex-none text-[11px] text-faint">
        {outgoing ? '→' : '←'} {other}
      </span>
    </button>
  );
}

function PropertyTable({ properties, keys, onSelect, selected }: { properties: Record<string, PropertySpec>; keys: Set<string>; onSelect: (name: string) => void; selected?: string }) {
  return (
    <div className="-mx-1.5">
      {Object.entries(properties).map(([name, p]) => (
        <button
          key={name}
          onClick={() => onSelect(name)}
          className={cn('flex w-full items-center gap-2 rounded-md px-1.5 py-0.5 text-left text-[12.5px] hover:bg-hover', selected === name && 'bg-accent-soft')}
        >
          <span className="flex w-3.5 justify-center">{keys.has(name) && <KeyRound size={11} className="text-changed" />}</span>
          <span className={cn('min-w-0 flex-1 truncate', !p.required && 'text-muted')}>{name}</span>
          <span className="font-mono text-[11px] text-muted">{typeLabel(p)}</span>
        </button>
      ))}
    </div>
  );
}

function Physical({ value }: { value: unknown }) {
  if (!value) return null;
  return (
    <Section title="Database details" defaultOpen={false}>
      <pre className="overflow-auto rounded-md bg-panel-2 p-2 font-mono text-[11px] leading-relaxed text-muted">{JSON.stringify(value, null, 2)}</pre>
    </Section>
  );
}

function EntityDetails({ loaded, keyPath, modelRef }: { loaded: LoadedModel; keyPath: string; modelRef: ModelRef }) {
  const model = loaded.result.doc.model;
  const loc = locate(model, keyPath);
  if (!loc) return <p className="p-3 text-muted">Not in this model.</p>;
  const root = model.entities[loc.node]!;
  const spec = loc.spec;
  const isOwned = loc.prefix !== '';
  const e = spec as EntitySpec & OwnedSpec;
  const derived = Object.entries(model.entities).filter(([, x]) => x.base === keyPath).map(([k]) => k);
  const relationships = model.relationships.map((r, i) => [r, i] as const).filter(([r]) => [r.dependent, r.principal].some((n) => n === keyPath || (!isOwned && n.startsWith(`${keyPath}.`))));
  const lines = loaded.result.lines.entities[keyPath];
  const keys = new Set(e.key ?? (isOwned ? e.ownerKey : []));
  return (
    <>
      <header className="border-b border-border px-3 py-3">
        <div className="flex items-center gap-2">
          {isOwned ? <Package size={15} className="text-faint" /> : <Table2 size={15} className="text-faint" />}
          <h2 className="truncate text-[14px] font-semibold">{keyPath}</h2>
        </div>
        {e.clrName && <p className="mt-0.5 truncate font-mono text-[11px] text-faint">{e.clrName}</p>}
        {e.comment && <p className="mt-1.5 text-[12.5px] text-muted">{e.comment}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <SourceLink file={loaded.file} line={lines?.[0]} />
          <button
            onClick={() => revealEntity(modelRef, loc.node, { focus: true })}
            className="flex items-center gap-1.5 text-[12px] text-accent hover:underline"
            title={`Show only ${loc.node} and what it relates to`}
          >
            <Crosshair size={13} /> Focus on {loc.node}
          </button>
          <button
            onClick={() => revealEntity(modelRef, loc.node, { deletion: true })}
            className="flex items-center gap-1.5 text-[12px] text-accent hover:underline"
            title={`What the database deletes, sets to null, or is stopped by when a ${loc.node} is deleted`}
          >
            <CascadeIcon size={13} /> What deleting does
          </button>
        </div>
      </header>
      <Section title="Overview">
        {isOwned && <Field label="Owned by">{loc.node} ({e.collection ? 'many' : 'one'}{e.required ? ', required' : ''})</Field>}
        {e.mapping?.table !== undefined && <Field label="Table">{e.mapping.table === null ? <span className="text-muted">none</span> : <Mono>{e.mapping.schema ? `${e.mapping.schema}.` : ''}{e.mapping.table}</Mono>}</Field>}
        {e.mapping?.view && <Field label="View"><Mono>{e.mapping.view}</Mono></Field>}
        {e.mapping?.json && <Field label="Stored as JSON in"><Mono>{e.mapping.json}</Mono></Field>}
        {!isOwned && (root as EntitySpec).base && (
          <Field label="Inherits">
            <button className="text-accent hover:underline" onClick={() => revealEntity(modelRef, (root as EntitySpec).base!)}>{(root as EntitySpec).base}</button>
          </Field>
        )}
        {e.mapping?.strategy && <Field label="Inheritance">{e.mapping.strategy.toUpperCase()}{e.mapping.discriminator && <> · discriminator <Mono>{e.mapping.discriminator}</Mono></>}</Field>}
        {e.mapping?.discriminatorValue !== undefined && <Field label="Discriminator value"><Mono>{JSON.stringify(e.mapping.discriminatorValue)}</Mono></Field>}
        {derived.length > 0 && (
          <Field label="Derived">
            {derived.map((d, i) => (
              <span key={d}>
                {i > 0 && ', '}
                <button className="text-accent hover:underline" onClick={() => revealEntity(modelRef, d)}>{d}</button>
              </span>
            ))}
          </Field>
        )}
        <Field label="Key">{e.key ? <Mono>{e.key.join(', ')}</Mono> : e.keyless ? <span className="text-muted">keyless</span> : isOwned ? <Mono>{e.ownerKey.join(', ')}</Mono> : <span className="text-muted">inherited</span>}</Field>
        {!isOwned && (e as EntitySpec).alternateKeys?.map((k) => <Field key={k.join()} label="Alternate key"><Mono>{k.join(', ')}</Mono></Field>)}
        {e.seed && <Field label="Seed rows">{e.seed.length}</Field>}
      </Section>
      <Section title="Properties" count={Object.keys(e.properties).length}>
        <PropertyTable
          properties={e.properties}
          keys={keys}
          onSelect={(name) => select(modelRef, { kind: 'property', entity: loc.node, property: `${loc.prefix}${name}` })}
        />
      </Section>
      {(e.owned || e.complex) && (
        <Section title="Owned and complex types">
          {Object.entries(e.owned ?? {}).map(([nav, o]) => (
            <button key={nav} onClick={() => select(modelRef, { kind: 'entity', key: `${keyPath}.${nav}` })} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover">
              <Package size={13} className="text-faint" /> {nav}
              <span className="ml-auto font-mono text-[11px] text-muted">{o.type}{o.collection ? '[]' : ''}{o.mapping?.json ? ' · json' : ''}</span>
            </button>
          ))}
          {Object.entries(e.complex ?? {}).map(([name, c]) => (
            <div key={name} className="flex items-center gap-2 px-1.5 py-1 text-[12.5px]">
              <Braces size={13} className="text-faint" /> {name}
              <span className="ml-auto font-mono text-[11px] text-muted">{c.type}{c.collection ? '[]' : ''}{c.mapping?.json ? ' · json' : ''}</span>
            </div>
          ))}
        </Section>
      )}
      {relationships.length > 0 && (
        <Section title="Relationships" count={relationships.length}>
          {relationships.map(([r, i]) => <RelationshipRow key={i} r={r} index={i} focus={keyPath} modelRef={modelRef} />)}
        </Section>
      )}
      {(e.indexes?.length ?? 0) > 0 && (
        <Section title="Indexes" count={e.indexes!.length}>
          {e.indexes!.map((ix, i) => (
            <div key={i} className="py-0.5 text-[12.5px]">
              <Mono>{ix.properties.join(', ')}</Mono>
              <span className="text-muted">
                {ix.unique && ' · unique'}
                {ix.filter && ` · where ${ix.filter}`}
                {ix.descending?.some(Boolean) && ' · descending'}
              </span>
            </div>
          ))}
        </Section>
      )}
      {(e.checks?.length ?? 0) > 0 && (
        <Section title="Check constraints" count={e.checks!.length}>
          {e.checks!.map((c) => (
            <Field key={c.name} label={c.name}><Mono>{c.sql}</Mono></Field>
          ))}
        </Section>
      )}
      {!isOwned && (
        <Section title="Mentioned in">
          <MentionedIn target={`entity:${modelRef.sketchId ? `sketch:${modelRef.sketchId}` : modelRef.setKey}|${keyPath}`} />
        </Section>
      )}
      {!isOwned && !modelRef.sketchId && (
        <Section title="History">
          <EntityHistory modelRef={modelRef} entityKey={keyPath} />
        </Section>
      )}
      <Physical value={e.physical} />
    </>
  );
}

function PropertyDetails({ loaded, entity, rowId, modelRef }: { loaded: LoadedModel; entity: string; rowId: string; modelRef: ModelRef }) {
  const model = loaded.result.doc.model;
  const spec = model.entities[entity];
  const found = spec ? resolveRow(spec, rowId) : null;
  if (!spec || !found) return <p className="p-3 text-muted">Not in this model.</p>;
  const { property: p, name, path } = found;
  const ownerPath = [entity, ...path].join('.');
  const usedIn = model.relationships.map((r, i) => [r, i] as const).filter(([r]) => r.dependent === ownerPath && r.foreignKey.includes(name));
  const line = loaded.result.lines.properties[`${ownerPath}.${name}`] ?? loaded.result.lines.properties[`${entity}.${rowId}`];
  return (
    <>
      <header className="border-b border-border px-3 py-3">
        <button className="text-[12px] text-muted hover:text-text" onClick={() => select(modelRef, { kind: 'entity', key: ownerPath })}>
          {ownerPath}
        </button>
        <h2 className="text-[14px] font-semibold">{name}</h2>
        <p className="mt-0.5 font-mono text-[12px] text-muted">{typeLabel(p)}</p>
        <div className="mt-2">
          <SourceLink file={loaded.file} line={line} />
        </div>
      </header>
      <Section title="Details">
        <Field label="Type"><Mono>{p.type}{p.collection ? '[]' : ''}</Mono></Field>
        <Field label="Required">{p.required ? 'yes' : 'no (nullable)'}</Field>
        {p.maxLength !== undefined && <Field label="Max length">{p.maxLength}</Field>}
        {p.precision !== undefined && <Field label="Precision">{p.precision}{p.scale !== undefined && `, scale ${p.scale}`}</Field>}
        {p.generated && <Field label="Generated">{{ onAdd: 'on insert', onUpdate: 'on update', onAddOrUpdate: 'on insert and update', onUpdateSometimes: 'on some updates' }[p.generated]}</Field>}
        {p.concurrencyToken && <Field label="Concurrency">concurrency token</Field>}
        {p.default !== undefined && <Field label="Default"><Mono>{JSON.stringify(p.default)}</Mono></Field>}
        {p.comment && <Field label="Comment">{p.comment}</Field>}
      </Section>
      {usedIn.length > 0 && (
        <Section title="Foreign key of">
          {usedIn.map(([r, i]) => <RelationshipRow key={i} r={r} index={i} focus={ownerPath} modelRef={modelRef} />)}
        </Section>
      )}
      <Physical value={p.physical} />
    </>
  );
}

function RelationshipDetails({ loaded, index, modelRef }: { loaded: LoadedModel; index: number; modelRef: ModelRef }) {
  const model = loaded.result.doc.model;
  const r = model.relationships[index];
  if (!r) return <p className="p-3 text-muted">Not in this model.</p>;
  return (
    <>
      <header className="border-b border-border px-3 py-3">
        <p className="text-[12px] text-muted">Relationship</p>
        <h2 className="flex items-center gap-2 text-[14px] font-semibold">
          <button className="hover:underline" onClick={() => revealEntity(modelRef, rootOf(r.dependent, model))}>{r.dependent}</button>
          <ArrowRight size={14} className="text-faint" />
          <button className="hover:underline" onClick={() => revealEntity(modelRef, rootOf(r.principal, model))}>{r.principal}</button>
        </h2>
      </header>
      <Section title="Details">
        <Field label="Cardinality">{r.cardinality === 'one-to-many' ? `One ${r.principal} has many ${r.dependent}` : `One ${r.principal} has one ${r.dependent}`}</Field>
        <Field label="Foreign key"><Mono>{r.foreignKey.join(', ')}</Mono></Field>
        {r.principalKey && <Field label="References"><Mono>{r.principalKey.join(', ')}</Mono></Field>}
        <Field label="Required">{r.required ? `yes, every ${r.dependent} needs a ${r.principal}` : 'no (optional)'}</Field>
        {r.requiredDependent && <Field label="Dependent required">yes</Field>}
        <Field label="On delete">{deleteLabel(r)}</Field>
        {r.dependentNavigation && <Field label={`${r.dependent} →`}><Mono>{r.dependentNavigation}</Mono></Field>}
        {r.principalNavigation && <Field label={`${r.principal} →`}><Mono>{r.principalNavigation}</Mono></Field>}
      </Section>
      <Physical value={r.physical} />
    </>
  );
}

function ModelSummary({ loaded }: { loaded: LoadedModel }) {
  const { doc, diagnostics, efVersion } = loaded.result;
  const warnings = diagnostics.filter((d) => d.severity !== 'info');
  return (
    <>
      <header className="border-b border-border px-3 py-3">
        <h2 className="text-[14px] font-semibold">{doc.title}</h2>
        <div className="mt-2">
          <SourceLink file={loaded.file} line={undefined} />
        </div>
      </header>
      <Section title="Model">
        <Field label="Entities">{Object.keys(doc.model.entities).length}</Field>
        <Field label="Relationships">{doc.model.relationships.length}</Field>
        {doc.model.defaultSchema && <Field label="Default schema"><Mono>{doc.model.defaultSchema}</Mono></Field>}
        {efVersion && <Field label="EF Core">{efVersion}</Field>}
      </Section>
      {warnings.length > 0 && (
        <Section title="Reading problems" count={warnings.length}>
          {warnings.map((d, i) => (
            <button key={i} onClick={() => openSourceFile(loaded.file, d.line)} className="block w-full rounded-md px-1.5 py-1 text-left text-[12px] text-changed hover:bg-hover">
              Line {d.line}: {d.message}
            </button>
          ))}
        </Section>
      )}
      <p className="px-3 py-3 text-[12px] text-faint">Select an entity, property or relationship in the diagram to see its details.</p>
    </>
  );
}

/** Back to the list of changes of the timeline step, from an entity or property. */
function BackToChanges({ name, onClick }: { name: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex h-8 w-full items-center gap-1.5 border-b border-border px-3 text-[12px] text-muted hover:bg-hover hover:text-text">
      <ArrowLeft size={13} /> Changes in {name}
    </button>
  );
}

/** What is selected in the tab in front (or, while a source file is in front, in the tab before it). */
export function Inspector() {
  const kind = useActiveTab((s) => tabKind(s.subject));
  const activeNote = useWorkspace((s) => s.activeNote);
  if (kind === 'codemap' || kind === 'endpoints' || kind === 'tests') return <CodeInspector />;
  if (kind === 'note' && activeNote) return <NoteInspector noteId={activeNote} />;
  return <ModelInspector />;
}

function ModelInspector() {
  const activeRef = useWorkspace((s) => s.activeRef);
  const activeStep = useWorkspace((s) => s.activeStep);
  const selection = useWorkspace((s) => s.selection);
  const timeline = useTimeline(activeStep?.setKey ?? null, false);
  const stepIndex = timeline && activeStep ? timeline.steps.findIndex((s) => s.id === activeStep.stepId) : -1;
  const ref = selection?.ref ?? activeRef;
  const loaded = useWorkspace((s) => (ref ? s.models[refKey(ref)] : undefined));
  const value = selection && ref && refKey(selection.ref) === refKey(ref) ? selection.value : null;
  const sketch = useWorkspace((s) => (ref?.sketchId ? s.sketches[ref.sketchId] : undefined));

  if (timeline && stepIndex >= 0 && (!value || selection?.ref.setKey !== activeStep?.setKey)) {
    return (
      <aside className="h-full overflow-y-auto" aria-label="Inspector">
        <ChangePanel timeline={timeline} index={stepIndex} />
      </aside>
    );
  }
  // Folder sketches are read only: their summary, or the usual details of what is selected.
  if (ref?.sketchId && sketch?.origin && !value) {
    return (
      <aside className="h-full overflow-y-auto" aria-label="Inspector">
        <SketchSummary sketch={sketch} />
      </aside>
    );
  }
  if (ref?.sketchId && sketch && !sketch.origin) {
    return (
      <aside className="h-full overflow-y-auto" aria-label="Inspector">
        <SketchInspector sketch={sketch} value={value} />
      </aside>
    );
  }
  if (!ref || !loaded) {
    return <p className="p-4 text-[12.5px] text-faint">Nothing selected.</p>;
  }
  return (
    <aside className="h-full overflow-y-auto" aria-label="Inspector">
      {timeline && stepIndex >= 0 && <BackToChanges name={timeline.steps[stepIndex]!.name} onClick={() => select(ref, null)} />}
      {!value && <ModelSummary loaded={loaded} />}
      {value?.kind === 'entity' && <EntityDetails loaded={loaded} keyPath={value.key} modelRef={ref} />}
      {value?.kind === 'property' && <PropertyDetails loaded={loaded} entity={value.entity} rowId={value.property} modelRef={ref} />}
      {value?.kind === 'relationship' && <RelationshipDetails loaded={loaded} index={value.index} modelRef={ref} />}
    </aside>
  );
}
